import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSiteAdmin } from '@/hooks/useSiteAdmin';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Wand2, Settings, User, ExternalLink, MapPin, Building, FileText, Users, Image } from 'lucide-react';
import { OnboardingBanner } from '@/components/onboarding/OnboardingBanner';
import { supabase } from '@/integrations/supabase/client';
import { useState } from 'react';

interface DashboardStats {
  townCount: number;
  areaCount: number;
  leadCount: number;
  blogCount: number;
  adCount: number;
}

export default function AgentDashboard() {
  const { user, loading: authLoading } = useAuth();
  const { site, isWizardComplete, loading: siteLoading } = useSiteAdmin();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({ 
    townCount: 0, areaCount: 0, leadCount: 0, blogCount: 0, adCount: 0 
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
      return;
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (site?.id) {
      fetchStats();
    }
  }, [site?.id]);

  const fetchStats = async () => {
    if (!site?.id) return;
    
    setLoading(true);
    try {
      const [townsRes, areasRes, leadsRes, blogRes, adsRes] = await Promise.all([
        supabase.from('towns').select('id', { count: 'exact', head: true }).eq('site_id', site.id).eq('is_published', true),
        supabase.from('areas').select('id', { count: 'exact', head: true }).eq('site_id', site.id).eq('is_published', true),
        supabase.from('leads').select('id', { count: 'exact', head: true }).eq('site_id', site.id),
        supabase.from('blog_posts').select('id', { count: 'exact', head: true }).eq('site_id', site.id),
        supabase.from('site_advertisements').select('id', { count: 'exact', head: true }).eq('site_id', site.id),
      ]);

      setStats({
        townCount: townsRes.count || 0,
        areaCount: areasRes.count || 0,
        leadCount: leadsRes.count || 0,
        blogCount: blogRes.count || 0,
        adCount: adsRes.count || 0,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const isLoading = authLoading || siteLoading || loading;

  if (isLoading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!user) {
    return null;
  }

  const previewUrl = site?.primary_domain 
    ? `https://${site.primary_domain}` 
    : window.location.origin;

  return (
    <AdminLayout title="Dashboard">
      {site?.id && <OnboardingBanner siteId={site.id} />}
      
      <div className="mb-6">
        <h1 className="text-2xl font-display font-bold">Welcome back!</h1>
        <p className="text-muted-foreground">
          Manage your real estate website for {site?.location_served}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => navigate('/admin/towns')}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Towns</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.townCount}</div>
            <p className="text-xs text-muted-foreground">Published</p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => navigate('/admin/areas')}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Areas</CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.areaCount}</div>
            <p className="text-xs text-muted-foreground">Published</p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => navigate('/admin/blog')}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Blog</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.blogCount}</div>
            <p className="text-xs text-muted-foreground">Posts</p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => navigate('/admin/ads')}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Ads</CardTitle>
            <Image className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.adCount}</div>
            <p className="text-xs text-muted-foreground">Configured</p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => navigate('/admin/leads')}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Leads</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.leadCount}</div>
            <p className="text-xs text-muted-foreground">Submissions</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {!isWizardComplete && (
            <Button 
              variant="default"
              className="h-auto py-4 flex flex-col items-center gap-2 col-span-2 md:col-span-4"
              onClick={() => navigate('/agent/wizard')}
            >
              <Wand2 className="h-6 w-6" />
              <span>Continue Setup Wizard</span>
            </Button>
          )}
          <Button 
            variant="outline" 
            className="h-auto py-4 flex flex-col items-center gap-2"
            onClick={() => navigate('/admin/profile')}
          >
            <User className="h-5 w-5" />
            <span className="text-xs">Profile</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-4 flex flex-col items-center gap-2"
            onClick={() => navigate('/admin/settings')}
          >
            <Settings className="h-5 w-5" />
            <span className="text-xs">Settings</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-4 flex flex-col items-center gap-2"
            onClick={() => window.open(previewUrl, '_blank')}
          >
            <ExternalLink className="h-5 w-5" />
            <span className="text-xs">View Site</span>
          </Button>
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
